import { store, getContext } from '@wordpress/interactivity';

interface ExplanationContext {
	isOpen: boolean;
	explanation: string;
}

let currentOpenTooltip: { anchor: HTMLElement; tooltip: HTMLElement } | null = null;

// Position tooltip within screen bounds
function positionTooltip(tooltip: HTMLElement, anchor: HTMLElement) {
	// Reset positioning classes and styles
	tooltip.classList.remove('tooltip-below');
	tooltip.style.left = '';
	tooltip.style.right = '';
	tooltip.style.transform = '';
	tooltip.style.bottom = '';
	tooltip.style.top = '';
	tooltip.style.marginBottom = '';
	tooltip.style.marginTop = '';

	// Force a reflow to get accurate measurements
	void tooltip.offsetHeight;

	const tooltipRect = tooltip.getBoundingClientRect();
	const anchorRect = anchor.getBoundingClientRect();
	const viewportWidth = window.innerWidth;
	const margin = 16; // Margin from viewport edge

	// Check if tooltip would overflow top
	if (tooltipRect.top < margin) {
		tooltip.classList.add('tooltip-below');
	}

	// Calculate horizontal positioning
	const tooltipWidth = tooltipRect.width;
	const anchorCenter = anchorRect.left + (anchorRect.width / 2);
	const idealLeft = anchorCenter - (tooltipWidth / 2);

	// Check horizontal boundaries
	if (idealLeft < margin) {
		// Too far left - align to left edge with margin
		tooltip.style.left = `${margin - anchorRect.left}px`;
		tooltip.style.transform = 'none';
	} else if (idealLeft + tooltipWidth > viewportWidth - margin) {
		// Too far right - align to right edge with margin
		tooltip.style.right = `${margin - (viewportWidth - anchorRect.right)}px`;
		tooltip.style.left = 'auto';
		tooltip.style.transform = 'none';
	}
}

// Close tooltip helper
function closeTooltip(anchor: HTMLElement, tooltip: HTMLElement) {
	tooltip.style.display = 'none';
	anchor.setAttribute('aria-expanded', 'false');
	if (currentOpenTooltip?.tooltip === tooltip) {
		currentOpenTooltip = null;
	}
}

store('aptum-blocks/explanation', {
	state: {},
	actions: {
		toggleTooltip(event: MouseEvent) {
			const context = getContext<ExplanationContext>();
			const target = event.target as HTMLElement;
			const anchor = target.closest('a[data-explanation]') as HTMLAnchorElement;

			if (!anchor) return;

			event.preventDefault();
			event.stopPropagation();

			// Close any currently open tooltip
			if (currentOpenTooltip && currentOpenTooltip.anchor !== anchor) {
				closeTooltip(currentOpenTooltip.anchor, currentOpenTooltip.tooltip);
			}

			// Get or create tooltip
			let tooltip = anchor.querySelector('.explanation-tooltip') as HTMLElement;

			if (!tooltip) {
				tooltip = document.createElement('div');
				tooltip.className = 'explanation-tooltip';
				tooltip.setAttribute('role', 'tooltip');
				tooltip.setAttribute('aria-live', 'polite');

				// Prevent clicks inside tooltip from closing it
				tooltip.onclick = (e) => {
					e.stopPropagation();
				};

				// Create close button
				const closeButton = document.createElement('button');
				closeButton.className = 'explanation-tooltip-close';
				closeButton.setAttribute('aria-label', 'Close explanation');
				closeButton.innerHTML = '&times;';
				closeButton.onclick = (e) => {
					e.preventDefault();
					e.stopPropagation();
					closeTooltip(anchor, tooltip);
				};

				// Create content wrapper
				const content = document.createElement('div');
				content.className = 'explanation-tooltip-content';

				// Add the word being explained in strong tags
				const wordElement = document.createElement('strong');
				wordElement.textContent = anchor.textContent || '';
				wordElement.style.display = 'block';

				// Add the explanation text
				const explanationElement = document.createElement('p');
				explanationElement.textContent = anchor.getAttribute('data-explanation') || '';

				content.appendChild(wordElement);
				content.appendChild(explanationElement);

				tooltip.appendChild(closeButton);
				tooltip.appendChild(content);
				anchor.appendChild(tooltip);
				anchor.setAttribute('aria-expanded', 'true');

				// Position tooltip within viewport
				requestAnimationFrame(() => positionTooltip(tooltip, anchor));

				currentOpenTooltip = { anchor, tooltip };
			} else {
				// Toggle visibility
				const isVisible = tooltip.style.display !== 'none';
				if (isVisible) {
					closeTooltip(anchor, tooltip);
				} else {
					tooltip.style.display = 'block';
					anchor.setAttribute('aria-expanded', 'true');

					// Position tooltip within viewport
					requestAnimationFrame(() => positionTooltip(tooltip, anchor));

					currentOpenTooltip = { anchor, tooltip };
				}
			}
		},

		handleKeydown(event: KeyboardEvent) {
			const key = event.key;

			// Activate on Enter or Space
			if (key === 'Enter' || key === ' ') {
				event.preventDefault();
				const target = event.target as HTMLElement;
				target.click();
			}

			// Close on Escape
			if (key === 'Escape') {
				const target = event.target as HTMLElement;
				const tooltip = target.querySelector('.explanation-tooltip') as HTMLElement;
				if (tooltip) {
					closeTooltip(target, tooltip);
				}
			}
		}
	},
	callbacks: {}
});

// Close tooltip when clicking outside
document.addEventListener('click', (event: MouseEvent) => {
	if (currentOpenTooltip) {
		const target = event.target as HTMLElement;
		// Check if click is outside the tooltip and anchor
		if (!currentOpenTooltip.anchor.contains(target) && !currentOpenTooltip.tooltip.contains(target)) {
			closeTooltip(currentOpenTooltip.anchor, currentOpenTooltip.tooltip);
		}
	}
});

// Reposition tooltip on window resize
window.addEventListener('resize', () => {
	if (currentOpenTooltip) {
		requestAnimationFrame(() => {
			if (currentOpenTooltip) {
				positionTooltip(currentOpenTooltip.tooltip, currentOpenTooltip.anchor);
			}
		});
	}
});
